import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { fetchPublicSystemStatus } from './api/debugController';
import AdminApp from './features/admin/AdminApp';
import { getCurrentSession, loginUser, signupUser } from './features/auth/authApi';
import AuthLayout from './features/auth/AuthLayout';
import AuthPanel from './features/auth/AuthPanel';
import InviteSetPasswordPage from './features/auth/InviteSetPasswordPage';
import SpApiSetup from './features/auth/SpApiSetup';
import Dashboard from './features/auth/Dashboard';
import DebugControllerPage from './features/debug/DebugControllerPage';
import SystemStatusGate from './features/debug/SystemStatusGate';
import { applyTheme, normalizeTheme, readStoredTheme } from './features/theme/themes';
import { clearProcurementDraft } from './features/orders/procurementDraft';
import { getDefaultAdminPath } from './features/admin/moduleAccess';

document.documentElement.setAttribute('data-theme', readStoredTheme());

const AUTH_TOKEN_KEY = 'crmAuthToken';
const DEBUG_CONTROLLER_PATHS = ['/debug-controller-4719', '/api/debug-controller-4719'];
const BOOTSTRAP_TIMEOUT_MS = 4000;

function abortAfter(ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return {
    signal: controller.signal,
    cancel: () => clearTimeout(timer),
  };
}

function isDebugControllerPath(pathname) {
  return DEBUG_CONTROLLER_PATHS.includes(pathname);
}

function DebugControllerRoute({ user, token, onSignOut, onSystemStatusChange }) {
  return (
    <DebugControllerPage
      user={user}
      token={token}
      onSignOut={onSignOut}
      onSystemStatusChange={onSystemStatusChange}
    />
  );
}

function debugControllerRoutes({ user, token, onSignOut, onSystemStatusChange }) {
  return DEBUG_CONTROLLER_PATHS.map((path) => (
    <Route
      key={path}
      path={path}
      element={
        <DebugControllerRoute
          user={user}
          token={token}
          onSignOut={onSignOut}
          onSystemStatusChange={onSystemStatusChange}
        />
      }
    />
  ));
}

const initialForm = {
  name: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
};

function needsSpApiSetup(user) {
  return user?.role === 'admin' && !user?.hasSpApiConfigured;
}

function AuthenticatedApp({
  session,
  onSignOut,
  onUserUpdate,
  onSpApiComplete,
  onSystemStatusChange,
  blockedPages,
}) {
  if (needsSpApiSetup(session.user)) {
    return (
      <Routes>
        {debugControllerRoutes({
          user: session.user,
          token: session.token,
          onSignOut,
          onSystemStatusChange,
        })}
        <Route
          path="*"
          element={
            <SpApiSetup
              token={session.token}
              onComplete={onSpApiComplete}
              onSignOut={onSignOut}
            />
          }
        />
      </Routes>
    );
  }

  if (session.user.role === 'super_admin') {
    return (
      <Routes>
        {debugControllerRoutes({
          user: session.user,
          token: session.token,
          onSignOut,
          onSystemStatusChange,
        })}
        <Route
          path="/*"
          element={
            <Dashboard
              user={session.user}
              token={session.token}
              onSignOut={onSignOut}
              onUserUpdate={onUserUpdate}
            />
          }
        />
      </Routes>
    );
  }

  // Seller admin + workspace staff share the admin shell (modules filter staff nav).
  if (session.user.role === 'admin' || session.user.role === 'workspace_user') {
    return (
      <Routes>
        {debugControllerRoutes({
          user: session.user,
          token: session.token,
          onSignOut,
          onSystemStatusChange,
        })}
        <Route
          path="/admin/*"
          element={
            <AdminApp
              user={session.user}
              token={session.token}
              onSignOut={onSignOut}
              onUserUpdate={onUserUpdate}
              blockedPages={blockedPages}
            />
          }
        />
        <Route path="*" element={<Navigate to={getDefaultAdminPath(session.user)} replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppShell({
  session,
  systemStatus,
  isBootstrapping,
  children,
  onSignOut,
  onUserUpdate,
  onSpApiComplete,
  onSystemStatusChange,
  onInviteAuthSuccess,
}) {
  const location = useLocation();
  const isDebugPath = isDebugControllerPath(location.pathname);
  const isInvitePath = location.pathname.startsWith('/invite/');

  if (isBootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Loading workspace…</p>
      </div>
    );
  }

  if (isInvitePath) {
    return (
      <Routes>
        <Route
          path="/invite/set-password"
          element={<InviteSetPasswordPage onAuthSuccess={onInviteAuthSuccess} />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <SystemStatusGate
      mode={systemStatus.mode}
      message={systemStatus.message}
      isDebugPath={isDebugPath}
      session={session}
    >
      {session ? (
        <AuthenticatedApp
          session={session}
          onSignOut={onSignOut}
          onUserUpdate={onUserUpdate}
          onSpApiComplete={onSpApiComplete}
          onSystemStatusChange={onSystemStatusChange}
          blockedPages={systemStatus.blockedPages || []}
        />
      ) : (
        children
      )}
    </SystemStatusGate>
  );
}

function App() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [session, setSession] = useState(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [systemStatus, setSystemStatus] = useState({
    mode: 'all_good',
    message: '',
    blockedPages: [],
  });

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);

      // Login must not wait on debug-controller / RDS. Show the form immediately
      // when there is no session token; refresh status in the background.
      if (!storedToken) {
        if (!cancelled) {
          setIsBootstrapping(false);
        }
        const statusWait = abortAfter(BOOTSTRAP_TIMEOUT_MS);
        try {
          const publicStatus = await fetchPublicSystemStatus({ signal: statusWait.signal });
          if (!cancelled && publicStatus?.mode) {
            setSystemStatus({
              mode: publicStatus.mode,
              message: publicStatus.message || '',
              blockedPages: publicStatus.blockedPages || [],
            });
          }
        } catch {
          // Keep default all_good if status endpoint is slow or down.
        } finally {
          statusWait.cancel();
        }
        return;
      }

      const sessionWait = abortAfter(BOOTSTRAP_TIMEOUT_MS);
      try {
        const [publicStatusResult, sessionResult] = await Promise.allSettled([
          fetchPublicSystemStatus({ signal: sessionWait.signal }),
          getCurrentSession(storedToken, { signal: sessionWait.signal }),
        ]);

        if (cancelled) {
          return;
        }

        if (publicStatusResult.status === 'fulfilled' && publicStatusResult.value?.mode) {
          setSystemStatus({
            mode: publicStatusResult.value.mode,
            message: publicStatusResult.value.message || '',
            blockedPages: publicStatusResult.value.blockedPages || [],
          });
        }

        if (sessionResult.status === 'fulfilled') {
          const payload = sessionResult.value;
          if (
            payload.user?.role === 'admin' ||
            payload.user?.role === 'super_admin' ||
            payload.user?.role === 'workspace_user'
          ) {
            applyTheme(normalizeTheme(payload.user.theme));
          }
          setSession({
            user: payload.user,
            token: payload.token || storedToken,
          });
        } else {
          localStorage.removeItem(AUTH_TOKEN_KEY);
        }
      } finally {
        sessionWait.cancel();
        if (!cancelled) {
          setIsBootstrapping(false);
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const submitLabel = useMemo(
    () => (mode === 'login' ? 'Sign in' : 'Create account'),
    [mode]
  );

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setStatus({ type: 'idle', message: '' });
    setForm((current) => ({
      ...initialForm,
      email: current.email,
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValue =
      name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value;
    setForm((current) => ({ ...current, [name]: nextValue }));
  };

  const handleSignOut = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    clearProcurementDraft();
    setSession(null);
    setMode('login');
    setForm(initialForm);
    setStatus({ type: 'idle', message: '' });
  };

  const establishSession = (payload) => {
    localStorage.setItem(AUTH_TOKEN_KEY, payload.token);
    if (payload.user?.role === 'admin' || payload.user?.role === 'super_admin' || payload.user?.role === 'workspace_user') {
      applyTheme(normalizeTheme(payload.user.theme));
    }
    setSession({
      user: payload.user,
      token: payload.token,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: 'idle', message: '' });

    try {
      if (mode === 'signup') {
        if (!/^\d{10}$/.test(form.phone || '')) {
          throw new Error('Phone number must be exactly 10 digits.');
        }
        if (form.password !== form.confirmPassword) {
          throw new Error('Password and confirm password must match.');
        }

        const payload = await signupUser(form);
        establishSession(payload);
        return;
      }

      const payload = await loginUser({
        email: form.email,
        password: form.password,
      });

      establishSession(payload);
    } catch (error) {
      setSession(null);
      setStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSpApiComplete = (updatedUser) => {
    setSession((current) => ({
      ...current,
      user: updatedUser,
    }));
  };

  const handleUserUpdate = (updatedUser) => {
    if (updatedUser?.theme) {
      applyTheme(normalizeTheme(updatedUser.theme));
    }
    setSession((current) => ({
      ...current,
      user: updatedUser,
    }));
  };

  const handleSystemStatusChange = (nextStatus) => {
    setSystemStatus({
      mode: nextStatus?.mode || 'all_good',
      message: nextStatus?.message || '',
      blockedPages: nextStatus?.blockedPages || [],
    });
  };

  useEffect(() => {
    let cancelled = false;

    async function refreshStatus() {
      try {
        const publicStatus = await fetchPublicSystemStatus();
        if (!cancelled && publicStatus?.mode) {
          setSystemStatus({
            mode: publicStatus.mode,
            message: publicStatus.message || '',
            blockedPages: publicStatus.blockedPages || [],
          });
        }
      } catch {
        // Ignore transient status poll failures.
      }
    }

    const timer = window.setInterval(refreshStatus, 20_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  return (
    <BrowserRouter>
      <AppShell
        session={session}
        systemStatus={systemStatus}
        isBootstrapping={isBootstrapping}
        onSignOut={handleSignOut}
        onUserUpdate={handleUserUpdate}
        onSpApiComplete={handleSpApiComplete}
        onSystemStatusChange={handleSystemStatusChange}
        onInviteAuthSuccess={establishSession}
      >
        <AuthLayout>
          <AuthPanel
            form={form}
            mode={mode}
            status={status}
            setStatus={setStatus}
            submitLabel={submitLabel}
            isSubmitting={isSubmitting}
            onChange={handleChange}
            onModeChange={handleModeChange}
            onSubmit={handleSubmit}
          />
        </AuthLayout>
      </AppShell>
    </BrowserRouter>
  );
}

export default App;
